/**
 * T-109: the QR "permanence ceremony" dialog.
 *
 * QR stickers are a permanent physical contract — a code is never destroyed,
 * only ARCHIVED (and it still resolves when scanned). Changing a code's status
 * is superadmin-only and deliberate: type the exact unit code AND give a reason.
 * The same ceremony gates re-activating an archived code (Master: reversible via
 * the same gate). Replaces the old hard-delete dialog.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { archiveQrCode, restoreQrCode } from '@/lib/qrCodeApi';
import type { UnitQrCode } from '@/types/qr';

type CeremonyMode = 'archive' | 'reactivate';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  unitCode: string;
  qrCode: UnitQrCode;
  mode: CeremonyMode;
  onDone: () => void;
}

const COPY: Record<CeremonyMode, { title: string; verb: string; busy: string; desc: string }> = {
  archive: {
    title: 'Archive QR code',
    verb: 'Archive',
    busy: 'Archiving…',
    desc: 'The code is NOT destroyed — it stays in the unit’s history and still resolves when scanned. Archiving takes it out of active use.',
  },
  reactivate: {
    title: 'Re-activate archived QR',
    verb: 'Re-activate',
    busy: 'Re-activating…',
    desc: 'Brings this archived code back into active use for the unit. Any currently-active code is retired automatically.',
  },
};

export function QrCeremonyDialog({ open, onOpenChange, unitId, unitCode, qrCode, mode, onDone }: Props) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset in the close handler rather than an effect (avoids a set-state-in-effect
  // cascade). The parent also unmounts the dialog on close, so this is belt-and-suspenders.
  function handleOpenChange(next: boolean) {
    if (!next) {
      setTyped('');
      setReason('');
      setSubmitting(false);
    }
    onOpenChange(next);
  }

  const copy = COPY[mode];
  const armed = typed.trim() === unitCode && reason.trim().length > 0;

  async function handleSubmit() {
    if (!armed) return;
    setSubmitting(true);
    const ceremony = { confirmUnitCode: typed.trim(), reason: reason.trim() };
    try {
      if (mode === 'archive') {
        await archiveQrCode(unitId, qrCode.id, ceremony);
        toast.success('QR code archived.');
      } else {
        await restoreQrCode(unitId, qrCode.id, ceremony);
        toast.success('QR code re-activated.');
      }
      onDone();
      handleOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `${copy.verb} failed.`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription className="space-y-2">
            <span>{copy.desc}</span>
            <span className="block">
              Type the unit code <span className="font-mono font-semibold">{unitCode}</span> and a
              reason to confirm.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cer-confirm">Unit code</Label>
            <Input
              id="cer-confirm"
              type="text"
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={unitCode}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cer-reason">Reason (required, ≤500 chars)</Label>
            <Input
              id="cer-reason"
              type="text"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. sticker damaged / unit re-labelled"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!armed || submitting}>
            {submitting ? copy.busy : copy.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
